import Constants from 'expo-constants';
import * as WebBrowser from 'expo-web-browser';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';
import { detectDeviceOrgLocale } from '../i18n/locale';
import { detectDeviceTimezone } from '../i18n/timezones';
import { trackEvent } from '../lib/analytics';
import { ANALYTICS_CONSENT_KEY } from '../lib/analyticsConsent';
import api, {
  setAccessTokenGetter,
  setForceTokenRefresh,
  setOnAuthFailure,
  setOnOrgAccessDenied,
} from '../lib/api';
import { useMixpanel } from '../lib/mixpanel';
import { clearPlanGates } from '../lib/plan-gate';
import { registerForPushNotifications } from '../lib/pushNotifications';
import { clearQueryCache, queryClient } from '../lib/queryPersistence';
import { secureStorage } from '../lib/secureStorage';
import { Sentry } from '../lib/sentry';
import { captureSignupAttribution } from '../lib/signupAttribution';
import { beginMagicLinkSend, magicLinkSent } from './magicLinkState';

// Detect if we're running in Expo Go
const isExpoGo = Constants.executionEnvironment === 'storeClient';

// Only import native modules when NOT in Expo Go and NOT on web
let Auth0Native: any = null;

if (!isExpoGo && Platform.OS !== 'web') {
  try {
    Auth0Native = require('react-native-auth0').default;
    if (__DEV__) console.log('Native modules loaded successfully');
  } catch (error) {
    console.warn('Native modules not available - using Expo auth fallback', error);
  }
} else if (__DEV__) {
  console.log(`Running in ${isExpoGo ? 'Expo Go' : 'web'} - using Expo auth session`);
}

// Warmup browser for faster auth on web
WebBrowser.maybeCompleteAuthSession();

const auth0Domain = process.env.EXPO_PUBLIC_AUTH0_DOMAIN!;
const auth0ClientId =
  Platform.OS === 'web'
    ? process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID_WEB || process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID!
    : process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID_NATIVE || process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID!;
const auth0Audience = process.env.EXPO_PUBLIC_AUTH0_AUDIENCE!;
const nativeIdentifier =
  (Platform.OS === 'ios'
    ? Constants.expoConfig?.ios?.bundleIdentifier
    : Constants.expoConfig?.android?.package) ??
  Constants.expoConfig?.ios?.bundleIdentifier ??
  Constants.expoConfig?.android?.package;
const auth0NativeCustomScheme = nativeIdentifier
  ? `${nativeIdentifier.toLowerCase()}.auth0`
  : undefined;
const auth0ExpectedNativeCallbackUri =
  nativeIdentifier && Platform.OS !== 'web'
    ? `${auth0NativeCustomScheme}://${auth0Domain}/${Platform.OS}/${nativeIdentifier.toLowerCase()}/callback`
    : null;

// Initialize Auth0 based on platform
const auth0Native = Auth0Native
  ? new Auth0Native({
      domain: auth0Domain,
      clientId: auth0ClientId,
    })
  : null;

interface Auth0User {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  email_verified?: boolean;
}

function decodeJwtPayload(token?: string): Record<string, any> | null {
  if (!token) return null;
  const [, payload] = token.split('.');
  if (!payload) return null;

  try {
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padding = normalized.length % 4;
    const padded = padding ? normalized + '='.repeat(4 - padding) : normalized;
    if (typeof globalThis.atob !== 'function') return null;
    const decoded = globalThis.atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

function getUserFromIdToken(idToken?: string): Auth0User | null {
  const payload = decodeJwtPayload(idToken);
  if (!payload?.sub) return null;

  return {
    sub: payload.sub,
    email: payload.email,
    name: payload.name,
    picture: payload.picture,
    email_verified: payload.email_verified,
  };
}

interface Organization {
  id: string;
  name: string;
  locale?: string;
  timezone?: string | null;
  createdAt: string;
}

interface BackendUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  platformRole?: string | null;
  organizations: Array<{
    organization: Organization;
    role: string;
  }>;
  organizationMembers?: Array<{
    organization: Organization;
    role: string;
  }>;
}

export type MagicLinkErrorCode =
  | 'link_invalid'
  | 'rate_limited'
  | 'invalid_email'
  | 'connection_disabled'
  | 'account_disabled'
  | 'email_taken'
  // An organization already exists for this email's company domain — the user must
  // be invited by an admin instead of creating a duplicate org (ORG_DOMAIN_EXISTS).
  | 'org_domain_exists'
  // The free plan has no open slots, so the access gate turned the new signup away
  // (FREE_CAPACITY_EXHAUSTED). The visitor belongs on the waitlist, not a retry.
  | 'free_capacity_exhausted'
  | 'invalid_code'
  | 'unknown';

export type MagicLinkState =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | {
      kind: 'sent';
      maskedEmail: string;
      lastSentAt: number;
      // FLOSEAI-439: a resend keeps this 'sent' screen mounted (no flash). While
      // the resend is in flight `resending` is true; once it lands `justResent`
      // drives the "code resent" confirmation message.
      resending?: boolean;
      justResent?: boolean;
    }
  | { kind: 'verifying' }
  | {
      kind: 'error';
      error_code: MagicLinkErrorCode;
      // Populated for 'org_domain_exists' so the UI can name the existing org and
      // the blocked domain in the message.
      organizationName?: string;
      domain?: string;
    };

type AuthMagicLinkOptions = { inviteToken?: string };

interface AuthContextType {
  auth0User: Auth0User | null;
  backendUser: BackendUser | null;
  currentOrganization: Organization | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  requestMagicLink: (email: string, options?: AuthMagicLinkOptions) => Promise<void>;
  submitMagicLinkCode: (code: string) => Promise<void>;
  magicLinkState: MagicLinkState;
  resetMagicLinkState: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  setCurrentOrganization: (org: Organization) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CREDENTIALS_KEY = 'auth0_credentials';
const BACKEND_USER_KEY = 'backend_user';
const CURRENT_ORG_KEY = 'current_organization';
const SIGNUP_DATA_KEY = 'signup_data';
export const PENDING_INVITE_KEY = 'pending_invite_token';
const MAGIC_LINK_CONNECTION = 'email';
const MAGIC_LINK_COOLDOWN_MS = 30_000;
const MAX_INVITE_TOKEN_LENGTH = 256;

// Headless E2E test-auth. When this build-time flag is set (non-production only),
// the magic-link UI is driven exactly as a real user would, but the two network
// calls are rerouted to the backend's test-auth endpoint instead of Auth0. Metro
// inlines EXPO_PUBLIC_* at build time, so this branch is stripped from normal builds.
const E2E_AUTH = process.env.EXPO_PUBLIC_E2E === 'true';
// Only these emails take the E2E test-auth path; every other address falls
// through to the real Auth0 flow, so E2E and normal login coexist on one build.
// Keep this list in sync with the backend's E2E_AUTH_EMAILS (same defaults).
const E2E_EMAILS = (
  process.env.EXPO_PUBLIC_E2E_EMAILS ?? 'e2e-user@flowsoft.test,e2e-admin@flowsoft.test'
)
  .split(',')
  .map((e: string) => e.trim().toLowerCase())
  .filter(Boolean);

function isE2EEmail(email: string): boolean {
  return E2E_AUTH && E2E_EMAILS.includes(email.trim().toLowerCase());
}

function cooldownKey(email: string): string {
  // SecureStore keys only allow [A-Za-z0-9._-]; emails contain "@" (and may
  // contain "+", etc.), so collapse every disallowed character to "_".
  const safeEmail = email.toLowerCase().replace(/[^a-z0-9._-]/g, '_');
  return `magic_link_cooldown.${safeEmail}`;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const visible = local.slice(0, 3);
  return `${visible}***@${domain}`;
}

type MagicLinkSendFailureCode =
  | 'rate_limited'
  | 'invalid_email'
  | 'connection_disabled'
  | 'unknown';

function mapAuth0ErrorToCode(error: any): MagicLinkSendFailureCode {
  const raw = (error?.error ?? error?.code ?? '').toString().toLowerCase();
  const description = (error?.error_description ?? error?.message ?? '').toString().toLowerCase();
  if (raw.includes('rate') || description.includes('rate') || description.includes('too many')) {
    return 'rate_limited';
  }
  if (description.includes('disabled') || description.includes('connection')) {
    return 'connection_disabled';
  }
  if (
    raw.includes('invalid_request') ||
    description.includes('invalid email') ||
    description.includes('valid email') ||
    description.includes('email address')
  ) {
    return 'invalid_email';
  }
  return 'unknown';
}

// Map a backend /auth/sync failure to a magic-link error code so the callback
// surfaces it instead of stranding the user on "check your inbox"
// (REQ-AUTH-ML-011 / REQ-AUTH-ML-013).
function mapSyncErrorToCode(error: any): MagicLinkErrorCode {
  // Backend bodies use camelCase `errorCode`; tolerate legacy `error_code` too.
  const backendCode =
    error?.response?.data?.errorCode ??
    error?.response?.data?.error_code ??
    error?.errorCode ??
    error?.error_code;
  switch (backendCode) {
    case 'AUTH_ACCOUNT_DISABLED':
      return 'account_disabled';
    case 'AUTH_EMAIL_TAKEN':
      return 'email_taken';
    case 'ORG_DOMAIN_EXISTS':
      return 'org_domain_exists';
    case 'FREE_CAPACITY_EXHAUSTED':
      return 'free_capacity_exhausted';
    default:
      return 'unknown';
  }
}

function normalizeOrganizations(rawOrgs: any[]) {
  return rawOrgs.map((org: any) =>
    org.organization
      ? org
      : {
          organization: { id: org.id, name: org.name, locale: org.locale, timezone: org.timezone },
          role: org.role,
        },
  );
}

// AsyncStorage is localStorage-backed on web, so a blanket localStorage.clear()
// on logout would also wipe the persisted analytics-consent choice — silently
// resetting an opted-out user to the opt-in default. Snapshot that one key and
// restore it after the clear. Native platforms never reach this path.
function clearWebStoragePreservingConsent(): void {
  try {
    const consent =
      typeof localStorage !== 'undefined' ? localStorage.getItem(ANALYTICS_CONSENT_KEY) : null;
    sessionStorage.clear();
    localStorage.clear();
    if (consent !== null) {
      localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
    }
  } catch {}
}

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const mp = useMixpanel();
  const [auth0User, setAuth0User] = useState<Auth0User | null>(null);
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null);
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // Marks that a real session exists. clearCredentials is re-entered by the API
  // interceptor for every in-flight request that 401s once the tokens are gone,
  // and the analytics event and the Mixpanel reset must not repeat for those.
  const hasSessionRef = useRef(false);
  const [magicLinkState, setMagicLinkState] = useState<MagicLinkState>({ kind: 'idle' });
  // Mirror of the latest magic-link state so requestMagicLink can tell a resend
  // (from the 'sent' screen) apart from a first send without re-creating the
  // callback on every state change (FLOSEAI-439).
  const magicLinkStateRef = useRef<MagicLinkState>(magicLinkState);
  magicLinkStateRef.current = magicLinkState;

  const useWebAuth = isExpoGo || Platform.OS === 'web' || !auth0Native;

  // Holds the in-flight OTP send context (email + optional invite) between the
  // /passwordless/start send and the submitMagicLinkCode verification.
  const pendingOtpRef = useRef<{ email: string; inviteToken?: string } | null>(null);

  // Helper to clear all credentials - defined first since other functions depend on it
  const clearCredentials = useCallback(async () => {
    await Promise.all([
      secureStorage.deleteItem(CREDENTIALS_KEY),
      secureStorage.deleteItem(BACKEND_USER_KEY),
      secureStorage.deleteItem(CURRENT_ORG_KEY),
      secureStorage.deleteItem(SIGNUP_DATA_KEY),
      secureStorage.deleteItem(PENDING_INVITE_KEY),
      secureStorage.deleteItem('app_language_preference'),
      clearQueryCache(),
    ]);

    if (Platform.OS === 'web') {
      clearWebStoragePreservingConsent();
    }

    setAuth0User(null);
    setBackendUser(null);
    setCurrentOrganizationState(null);
    setAccessToken(null);
    // Reset the magic-link flow so the login screen renders the email input
    // instead of a stale OTP code-entry form after logout.
    pendingOtpRef.current = null;
    setMagicLinkState({ kind: 'idle' });
    Sentry.setUser(null);

    // The storage teardown above is idempotent and must run on every call — it
    // also clears a foreign session found at startup. The analytics event is
    // not: repeating it once per in-flight request that 401s on the way out
    // inflates every logout count.
    if (hasSessionRef.current) {
      hasSessionRef.current = false;
      trackEvent(mp, 'auth.user_logged_out');
    }

    // Reset unconditionally, and always after the storage clear. On web that
    // clear wipes localStorage, which is where the Mixpanel client keeps its
    // anonymous id — so a teardown that clears without resetting leaves the id
    // minted by an earlier one unpersisted, and the next page load invents
    // another. Skipping the reset here would only move the fragmentation this
    // guard exists to prevent. Ordered after the event so the logout is still
    // attributed to the session that ended.
    mp.reset();
  }, [mp]);

  // Refresh token function - defined before getAccessToken since it's called by it
  const refreshAccessToken = useCallback(
    async (refreshToken: string): Promise<string | null> => {
      try {
        let newAccessToken: string;

        if (isExpoGo || Platform.OS === 'web' || !auth0Native) {
          const tokenResponse = await fetch(`https://${auth0Domain}/oauth/token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              grant_type: 'refresh_token',
              client_id: auth0ClientId,
              refresh_token: refreshToken,
              audience: auth0Audience,
            }),
          });

          const data = await tokenResponse.json();
          newAccessToken = data.access_token;

          await secureStorage.setItem(
            CREDENTIALS_KEY,
            JSON.stringify({
              accessToken: data.access_token,
              refreshToken: data.refresh_token || refreshToken,
              idToken: data.id_token,
              expiresAt: Date.now() + (data.expires_in || 3600) * 1000,
            }),
          );
        } else {
          const credentials = await auth0Native.auth.refreshToken({ refreshToken });
          newAccessToken = credentials.accessToken;

          await secureStorage.setItem(
            CREDENTIALS_KEY,
            JSON.stringify({
              accessToken: credentials.accessToken,
              refreshToken: credentials.refreshToken || refreshToken,
              idToken: credentials.idToken,
              expiresAt: Date.now() + (credentials.expiresIn || 3600) * 1000,
            }),
          );
        }

        setAccessToken(newAccessToken);
        hasSessionRef.current = true;
        return newAccessToken;
      } catch (error) {
        console.error('Token refresh failed:', error);
        Sentry.captureException(error);

        // If refresh fails, clear credentials
        await clearCredentials();
        return null;
      }
    },
    [clearCredentials],
  );

  const forceRefreshAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const storedCreds = await secureStorage.getItem(CREDENTIALS_KEY);
      if (!storedCreds) return null;

      const credentials = JSON.parse(storedCreds);
      if (credentials.refreshToken) {
        return await refreshAccessToken(credentials.refreshToken);
      }
      return null;
    } catch (error) {
      console.error('Force token refresh failed:', error);
      return null;
    }
  }, [refreshAccessToken]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const storedCreds = await secureStorage.getItem(CREDENTIALS_KEY);
      if (!storedCreds) return null;

      const credentials = JSON.parse(storedCreds);

      // Check if token is expired (with 5 min buffer)
      const now = Date.now();
      if (credentials.expiresAt && credentials.expiresAt - now < 5 * 60 * 1000) {
        // Token expired or expiring soon, refresh it
        if (credentials.refreshToken) {
          const newToken = await refreshAccessToken(credentials.refreshToken);
          return newToken;
        }
        return null;
      }

      return credentials.accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      Sentry.captureException(error);
      return null;
    }
  }, [refreshAccessToken]);

  const initRef = useRef(false);
  const getAccessTokenRef = useRef(getAccessToken);
  const forceRefreshRef = useRef(forceRefreshAccessToken);
  const clearCredentialsRef = useRef(clearCredentials);
  getAccessTokenRef.current = getAccessToken;
  forceRefreshRef.current = forceRefreshAccessToken;
  clearCredentialsRef.current = clearCredentials;

  const registerApiInterceptors = useCallback(() => {
    setAccessTokenGetter((...args: Parameters<typeof getAccessToken>) =>
      getAccessTokenRef.current(...args),
    );
    setForceTokenRefresh((...args: Parameters<typeof forceRefreshAccessToken>) =>
      forceRefreshRef.current(...args),
    );
    setOnAuthFailure(() => clearCredentialsRef.current());
    setOnOrgAccessDenied(() => handleOrgAccessDeniedRef.current());
  }, []);

  const syncUserWithBackend = useCallback(
    async (token: string, userInfo: Auth0User, options?: { inviteToken?: string }) => {
      try {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Syncing user with backend',
          level: 'info',
        });

        let inviteToken: string | undefined = options?.inviteToken;

        if (!inviteToken) {
          try {
            const signupData = await secureStorage.getItem(SIGNUP_DATA_KEY);
            if (signupData) {
              const parsed = JSON.parse(signupData);
              inviteToken = parsed.inviteToken;
              await secureStorage.deleteItem(SIGNUP_DATA_KEY);
            }
          } catch {
            // ignore parsing errors
          }
        }

        if (!inviteToken) {
          const pendingInviteToken = await secureStorage.getItem(PENDING_INVITE_KEY);
          if (pendingInviteToken) {
            inviteToken = pendingInviteToken;
            await secureStorage.deleteItem(PENDING_INVITE_KEY);
          }
        }

        const deviceTimezone = detectDeviceTimezone();
        const syncResponse = await api.post(
          '/auth/sync',
          {
            auth0Id: userInfo.sub,
            email: userInfo.email || '',
            fullName: userInfo.name || userInfo.email || 'User',
            locale: detectDeviceOrgLocale(),
            ...(deviceTimezone ? { timezone: deviceTimezone } : {}),
            ...(inviteToken ? { inviteToken } : {}),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const rawUser = syncResponse.data.user;
        const organizations = syncResponse.data.organizations;

        const normalizedOrgs = normalizeOrganizations(
          rawUser.organizationMembers ?? organizations ?? [],
        );

        const user = {
          ...rawUser,
          organizations: normalizedOrgs,
        };

        setBackendUser(user);
        mp.identify(user.id, {
          $email: userInfo.email ?? '',
          $name: user.fullName,
          role: user.role,
          platform: Platform.OS,
        });
        mp.register({
          app_platform: Platform.OS,
          user_role: user.role,
        });

        await secureStorage.setItem(BACKEND_USER_KEY, JSON.stringify(user));

        if (organizations && organizations.length > 0) {
          const org = organizations[0];
          const defaultOrg = {
            id: org.id,
            name: org.name,
            locale: org.locale,
            createdAt: org.createdAt || new Date().toISOString(),
          };
          setCurrentOrganizationState(defaultOrg);
          mp.setGroup('organization', defaultOrg.id, {
            name: defaultOrg.name,
          });
          await secureStorage.setItem(CURRENT_ORG_KEY, JSON.stringify(defaultOrg));
        }

        await acceptPendingInviteIfNeeded();

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'User synced with backend successfully',
          level: 'info',
        });

        return {
          isMigration: syncResponse.data.is_migration === true,
          isNewUser: syncResponse.data.is_new_user === true,
        };
      } catch (error) {
        console.error('Failed to sync user with backend:', error);
        Sentry.captureException(error);
        throw error;
      }
    },
    [mp],
  );

  const acceptPendingInviteIfNeeded = useCallback(async () => {
    try {
      const pendingToken = await secureStorage.getItem(PENDING_INVITE_KEY);
      if (!pendingToken) return;

      await secureStorage.deleteItem(PENDING_INVITE_KEY);

      const { data } = await api.post(`/organizations/invites/${pendingToken}/accept`);

      const meResponse = await api.get('/auth/me');
      if (meResponse.data?.user) {
        const rawOrgs =
          meResponse.data.user.organizationMembers ?? meResponse.data.organizations ?? [];
        const updatedUser = {
          ...meResponse.data.user,
          organizations: normalizeOrganizations(rawOrgs),
        };
        setBackendUser(updatedUser);
        await secureStorage.setItem(BACKEND_USER_KEY, JSON.stringify(updatedUser));
      }

      if (data.organization) {
        const newOrg = {
          id: data.organizationId,
          name: data.organization.name,
          createdAt: data.organization.createdAt || new Date().toISOString(),
        };
        setCurrentOrganizationState(newOrg);
        mp.setGroup('organization', newOrg.id, { name: newOrg.name });
        await secureStorage.setItem(CURRENT_ORG_KEY, JSON.stringify(newOrg));
      }

      // Membership just changed server-side; drop any stale team caches so the
      // new member sees themselves (and the owner sees them) without a relogin.
      // Fall back to broad keys if the response omitted the org id, since a
      // narrow ['members', undefined] would match no registered query.
      const orgKey = data.organizationId ? ['members', data.organizationId] : ['members'];
      const inviteKey = data.organizationId ? ['invites', data.organizationId] : ['invites'];
      queryClient.invalidateQueries({ queryKey: orgKey });
      queryClient.invalidateQueries({ queryKey: inviteKey });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Pending invite accepted automatically after login',
        level: 'info',
      });
    } catch (error) {
      // A 409 means the membership was already created during /auth/sync, so the
      // accept short-circuits. The "member AND pending" state still needs
      // reconciling — invalidate the team caches broadly (org id unknown here).
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      console.warn('Failed to auto-accept pending invite:', error);
    }
  }, [mp]);

  // Foreign-session guard (REQ-AUTH-ML-012): clear all local state when the incoming
  // identity differs from the one already stored, before writing the new tokens.
  const clearLocalSessionIfForeign = useCallback(async (incomingAuth0Id?: string) => {
    if (!incomingAuth0Id) return;
    const storedUser = await secureStorage.getItem(BACKEND_USER_KEY);
    if (!storedUser) return;
    try {
      const parsed = JSON.parse(storedUser);
      if (parsed?.auth0Id && parsed.auth0Id !== incomingAuth0Id) {
        await clearCredentials();
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  const finishMagicLinkSync = useCallback(
    async (token: string, userInfo: Auth0User, inviteToken?: string) => {
      trackEvent(mp, 'auth.magic_link_consumed', { has_invite: !!inviteToken });
      try {
        const result = await syncUserWithBackend(token, userInfo, { inviteToken });
        trackEvent(mp, 'auth.login_succeeded', {
          connection: MAGIC_LINK_CONNECTION,
          is_migration: result?.isMigration === true,
          is_new_user: result?.isNewUser === true,
        });
      } catch (error: any) {
        const errorCode = mapSyncErrorToCode(error);
        const data = error?.response?.data;
        trackEvent(mp, 'auth.login_failed', {
          connection: MAGIC_LINK_CONNECTION,
          error_code: data?.errorCode ?? data?.error_code ?? 'unknown',
        });
        // Surface backend sync failures (account disabled / email taken / domain
        // already claimed / unknown) instead of stranding the user on "check your
        // inbox" (REQ-AUTH-ML-011 / REQ-AUTH-ML-013). For 'org_domain_exists' carry
        // the existing org's name + domain so the UI can explain who to ask.
        setMagicLinkState({
          kind: 'error',
          error_code: errorCode,
          ...(errorCode === 'org_domain_exists' && {
            organizationName: data?.organizationName,
            domain: data?.domain,
          }),
        });
        throw error;
      }
    },
    [mp, syncUserWithBackend],
  );

  const resolveUserInfoFromWeb = async (token: string): Promise<Auth0User> => {
    const userInfoResponse = await fetch(`https://${auth0Domain}/userinfo`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!userInfoResponse.ok) {
      throw new Error(`Auth0 /userinfo failed with status ${userInfoResponse.status}`);
    }

    return userInfoResponse.json();
  };

  const resolveUserInfoFromNative = async (credentials: {
    accessToken: string;
    idToken?: string;
  }): Promise<Auth0User> => {
    let userInfo = getUserFromIdToken(credentials.idToken);

    try {
      const profile = await auth0Native.auth.userInfo({ token: credentials.accessToken });
      userInfo = profile;
    } catch (userInfoError) {
      if (!userInfo) throw userInfoError;
      console.warn(
        'Native /userinfo rejected access token. Falling back to idToken claims.',
        userInfoError,
      );
    }

    if (!userInfo?.sub) {
      throw new Error('Unable to resolve authenticated user profile');
    }

    return userInfo;
  };

  // Complete passwordless login in-session by submitting the OTP code Auth0
  // emailed. Web/Expo-Go exchanges via the passwordless OTP grant at
  // /oauth/token; native uses the SDK's loginWithEmail (D-SEND-2).
  const submitMagicLinkCode = useCallback(
    async (code: string) => {
      const pending = pendingOtpRef.current;
      if (!pending) {
        setMagicLinkState({ kind: 'error', error_code: 'unknown' });
        return;
      }

      const { email, inviteToken } = pending;
      setMagicLinkState({ kind: 'verifying' });

      // The only signal that the visitor came back from their inbox and actually
      // typed something. Without it, giving up on a mistyped code and never
      // opening the email are the same silence.
      trackEvent(mp, 'auth.otp_submitted', { has_invite: !!inviteToken });

      // E2E: exchange the fixed code with the backend test-auth endpoint (no Auth0,
      // no /auth/sync). The seeded user already exists, so the response carries the
      // backend user + orgs directly and we hydrate the session from it.
      if (isE2EEmail(email)) {
        try {
          const { data } = await api.post('/auth/e2e/login', { email, code });

          await clearLocalSessionIfForeign(data.auth0User?.sub);

          await secureStorage.setItem(
            CREDENTIALS_KEY,
            JSON.stringify({
              accessToken: data.access_token,
              expiresAt: Date.now() + (data.expires_in || 3600) * 1000,
            }),
          );

          registerApiInterceptors();
          setAccessToken(data.access_token);
          hasSessionRef.current = true;
          setAuth0User(data.auth0User);

          const normalizedOrgs = normalizeOrganizations(
            data.user?.organizationMembers ?? data.organizations ?? [],
          );
          const user = { ...data.user, organizations: normalizedOrgs };
          setBackendUser(user);
          await secureStorage.setItem(BACKEND_USER_KEY, JSON.stringify(user));

          if (data.organizations?.length > 0) {
            const org = data.organizations[0];
            const defaultOrg = {
              id: org.id,
              name: org.name,
              locale: org.locale,
              createdAt: org.createdAt || new Date().toISOString(),
            };
            setCurrentOrganizationState(defaultOrg);
            await secureStorage.setItem(CURRENT_ORG_KEY, JSON.stringify(defaultOrg));
          }

          pendingOtpRef.current = null;
          Sentry.setUser({ id: data.auth0User?.sub });
        } catch (error: any) {
          Sentry.captureException(error);
          setMagicLinkState({ kind: 'error', error_code: 'invalid_code' });
        }
        return;
      }

      try {
        let token: string;
        let idToken: string | undefined;
        let refreshToken: string | undefined;
        let expiresIn: number | undefined;

        if (useWebAuth) {
          const tokenResponse = await fetch(`https://${auth0Domain}/oauth/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              grant_type: 'http://auth0.com/oauth/grant-type/passwordless/otp',
              client_id: auth0ClientId,
              username: email,
              otp: code,
              realm: MAGIC_LINK_CONNECTION,
              audience: auth0Audience,
              scope: 'openid profile email offline_access',
            }),
          });

          if (!tokenResponse.ok) {
            let errorBody: any = {};
            try {
              errorBody = await tokenResponse.json();
            } catch {
              // non-JSON error body — fall through to unknown mapping
            }
            throw errorBody;
          }

          const data = await tokenResponse.json();
          token = data.access_token;
          idToken = data.id_token;
          refreshToken = data.refresh_token;
          expiresIn = data.expires_in;
        } else {
          const creds = await auth0Native.auth.loginWithEmail({
            email,
            code,
            audience: auth0Audience,
            scope: 'openid profile email offline_access',
          });
          token = creds.accessToken;
          idToken = creds.idToken;
          refreshToken = creds.refreshToken;
          expiresIn = creds.expiresIn;
        }

        const userInfo = useWebAuth
          ? await resolveUserInfoFromWeb(token)
          : await resolveUserInfoFromNative({ accessToken: token, idToken });

        await clearLocalSessionIfForeign(userInfo?.sub);

        await secureStorage.setItem(
          CREDENTIALS_KEY,
          JSON.stringify({
            accessToken: token,
            refreshToken,
            idToken,
            expiresAt: Date.now() + (expiresIn || 3600) * 1000,
          }),
        );

        registerApiInterceptors();
        setAccessToken(token);
        hasSessionRef.current = true;
        setAuth0User(userInfo);

        await finishMagicLinkSync(token, userInfo, inviteToken);

        pendingOtpRef.current = null;
        Sentry.setUser({ id: userInfo.sub });
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'User logged in successfully via OTP code',
          level: 'info',
        });
      } catch (error: any) {
        const reason = (error?.error ?? error?.code ?? '').toString().toLowerCase();
        // A blocked Auth0 account (`blocked_user`) is NOT a bad code — surface the
        // meaningful "account disabled" copy instead of "try again".
        const accountBlocked = reason.includes('blocked');
        const invalidCode =
          reason.includes('invalid_grant') || reason.includes('otp') || reason.includes('expired');
        trackEvent(mp, 'auth.otp_failed', {
          error_code: accountBlocked
            ? 'account_disabled'
            : invalidCode
              ? 'invalid_code'
              : 'unknown',
        });
        Sentry.captureException(error);
        // Functional updater so finishMagicLinkSync's account_disabled/email_taken
        // code is preserved rather than clobbered by the generic mapping.
        setMagicLinkState((prev) =>
          prev.kind === 'error'
            ? prev
            : {
                kind: 'error',
                error_code: accountBlocked
                  ? 'account_disabled'
                  : invalidCode
                    ? 'invalid_code'
                    : 'unknown',
              },
        );
      }
    },
    [useWebAuth, clearLocalSessionIfForeign, registerApiInterceptors, finishMagicLinkSync, mp],
  );

  const restoreAuth0Credentials = useCallback(async (storedCreds: string) => {
    let credentials = JSON.parse(storedCreds);

    const now = Date.now();
    const isExpired = credentials.expiresAt && credentials.expiresAt - now < 5 * 60 * 1000;

    if (isExpired && credentials.refreshToken) {
      const newToken = await refreshAccessToken(credentials.refreshToken);
      if (!newToken) return;

      const updatedCreds = await secureStorage.getItem(CREDENTIALS_KEY);
      if (updatedCreds) credentials = JSON.parse(updatedCreds);
    }

    registerApiInterceptors();
    setAccessToken(credentials.accessToken);
    hasSessionRef.current = true;

    const useWeb = isExpoGo || Platform.OS === 'web' || !auth0Native;
    const userInfo = useWeb
      ? await resolveUserInfoFromWeb(credentials.accessToken)
      : await resolveUserInfoFromNative(credentials);

    setAuth0User(userInfo);
    Sentry.setUser({ id: userInfo.sub });
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Loaded stored Auth0 credentials',
      level: 'info',
    });
  }, []);

  const restoreBackendUser = useCallback(
    (storedUser: string) => {
      const user = JSON.parse(storedUser);
      setBackendUser(user);
      mp.register({ app_platform: Platform.OS, user_role: user.role });
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Loaded stored backend user',
        level: 'info',
      });
    },
    [mp],
  );

  const restoreOrganization = useCallback(
    (storedOrg: string) => {
      const org = JSON.parse(storedOrg);
      setCurrentOrganizationState(org);
      mp.setGroup('organization', org.id, { name: org.name });
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Loaded stored organization',
        level: 'info',
      });
    },
    [mp],
  );

  const loadStoredCredentials = useCallback(async () => {
    try {
      setIsLoading(true);

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Loading stored credentials',
        level: 'info',
      });

      const [storedCreds, storedUser, storedOrg] = await Promise.all([
        secureStorage.getItem(CREDENTIALS_KEY),
        secureStorage.getItem(BACKEND_USER_KEY),
        secureStorage.getItem(CURRENT_ORG_KEY),
      ]);

      if (storedCreds) await restoreAuth0Credentials(storedCreds);
      if (storedUser) restoreBackendUser(storedUser);
      if (storedOrg) restoreOrganization(storedOrg);
    } catch (error) {
      console.error('Error loading credentials:', error);
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  }, [restoreAuth0Credentials, restoreBackendUser, restoreOrganization]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    void loadStoredCredentials();
  }, [loadStoredCredentials]);

  useLayoutEffect(() => {
    if (accessToken) {
      registerApiInterceptors();
    }
  }, [accessToken, registerApiInterceptors]);

  const resetMagicLinkState = useCallback(() => {
    setMagicLinkState({ kind: 'idle' });
  }, []);

  const requestMagicLink = useCallback(
    async (email: string, options?: AuthMagicLinkOptions) => {
      const inviteToken = options?.inviteToken;

      if (inviteToken && inviteToken.length > MAX_INVITE_TOKEN_LENGTH) {
        setMagicLinkState({ kind: 'error', error_code: 'unknown' });
        return;
      }

      const hasInvite = !!inviteToken;
      // A resend keeps the code-entry screen mounted; only a first send shows
      // the email/requesting phase (FLOSEAI-439, AC#1).
      const wasResend = magicLinkStateRef.current.kind === 'sent';
      setMagicLinkState(beginMagicLinkSend(magicLinkStateRef.current));

      // E2E: no Auth0 email is sent. Stash the pending context and jump straight to
      // the code-entry screen so the flow is identical from the user's point of view.
      if (isE2EEmail(email)) {
        pendingOtpRef.current = { email, inviteToken };
        setMagicLinkState(magicLinkSent(maskEmail(email), Date.now(), wasResend));
        return;
      }

      // Persisted 30s cooldown (REQ-AUTH-ML-007) — survives app restart.
      const storedCooldown = await secureStorage.getItem(cooldownKey(email));
      if (storedCooldown) {
        const lastSentAt = Number(storedCooldown);
        if (Number.isFinite(lastSentAt) && Date.now() - lastSentAt < MAGIC_LINK_COOLDOWN_MS) {
          setMagicLinkState({ kind: 'error', error_code: 'rate_limited' });
          return;
        }
      }

      try {
        clearPlanGates();
        trackEvent(mp, 'auth.magic_link_requested', { has_invite: hasInvite });

        // Hand the campaign to the server before the visitor leaves for their
        // inbox: the code often gets typed back in a different browser, where
        // none of the client-side attribution exists. Never awaited — the login
        // code must not wait on a reporting call.
        void captureSignupAttribution(email, () => mp?.getDeviceId?.());

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'auth.magic_link_requested',
          level: 'info',
          data: { has_invite: hasInvite },
        });

        // Stash the in-flight context so submitMagicLinkCode can complete login
        // once the user types the emailed OTP code (D-SEND-2).
        pendingOtpRef.current = { email, inviteToken };

        // OTP passwordless: ask Auth0 to email a 6-digit code. This does NOT log
        // the user in — login completes in submitMagicLinkCode.
        const startResponse = await fetch(`https://${auth0Domain}/passwordless/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: auth0ClientId,
            connection: MAGIC_LINK_CONNECTION,
            email,
            send: 'code',
          }),
        });

        if (!startResponse.ok) {
          let errorBody: any = {};
          try {
            errorBody = await startResponse.json();
          } catch {
            // non-JSON error body — fall through to unknown mapping
          }
          throw errorBody;
        }

        const lastSentAt = Date.now();
        // Best-effort: the code is already on its way, so a storage failure
        // must never surface as a send error to the user.
        try {
          await secureStorage.setItem(cooldownKey(email), String(lastSentAt));
        } catch (storageError) {
          Sentry.captureException(storageError);
        }
        trackEvent(mp, wasResend ? 'auth.magic_link_resent' : 'auth.magic_link_sent', {
          has_invite: hasInvite,
        });
        setMagicLinkState(magicLinkSent(maskEmail(email), lastSentAt, wasResend));
      } catch (error: any) {
        const errorCode = mapAuth0ErrorToCode(error);
        trackEvent(mp, 'auth.magic_link_send_failed', {
          error_code: errorCode,
          ...(error?.error ? { auth0_error_code: String(error.error) } : {}),
        });
        Sentry.captureException(error);
        setMagicLinkState({ kind: 'error', error_code: errorCode });
      }
    },
    [mp],
  );

  const logout = useCallback(async () => {
    try {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Logout initiated',
        level: 'info',
      });

      setIsLoading(true);

      if (!isExpoGo && Platform.OS !== 'web' && auth0Native) {
        await auth0Native.webAuth.clearSession(
          auth0ExpectedNativeCallbackUri ? { returnToUrl: auth0ExpectedNativeCallbackUri } : {},
          auth0NativeCustomScheme ? { customScheme: auth0NativeCustomScheme } : undefined,
        );
      }

      await clearCredentials();

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Logout successful',
        level: 'info',
      });
    } catch (error) {
      console.error('Logout error:', error);
      Sentry.captureException(error);
    } finally {
      clearPlanGates();
      setIsLoading(false);
    }
  }, [clearCredentials]);

  const setCurrentOrganization = useCallback(
    async (org: Organization) => {
      setCurrentOrganizationState(org);
      mp.setGroup('organization', org.id, { name: org.name });
      await secureStorage.setItem(CURRENT_ORG_KEY, JSON.stringify(org));

      Sentry.addBreadcrumb({
        category: 'auth',
        message: `Organization changed to: ${org.name}`,
        level: 'info',
      });
    },
    [mp],
  );

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      if (data?.user) {
        const rawOrgs = data.user.organizationMembers ?? data.organizations ?? [];
        const user = { ...data.user, organizations: normalizeOrganizations(rawOrgs) };
        setBackendUser(user);
        await secureStorage.setItem(BACKEND_USER_KEY, JSON.stringify(user));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const orgDeniedInFlight = useRef(false);
  const handleOrgAccessDenied = useCallback(async () => {
    if (orgDeniedInFlight.current) return;
    orgDeniedInFlight.current = true;
    try {
      const { data } = await api.get('/auth/me');
      if (!data?.user) return;

      const rawOrgs = data.user.organizationMembers ?? data.organizations ?? [];
      const orgs = normalizeOrganizations(rawOrgs);
      const user = { ...data.user, organizations: orgs };
      setBackendUser(user);
      await secureStorage.setItem(BACKEND_USER_KEY, JSON.stringify(user));

      if (orgs.length > 0) {
        const firstOrg = orgs[0].organization;
        const fallbackOrg = {
          id: firstOrg.id,
          name: firstOrg.name,
          createdAt: firstOrg.createdAt || new Date().toISOString(),
        };
        setCurrentOrganizationState(fallbackOrg);
        await secureStorage.setItem(CURRENT_ORG_KEY, JSON.stringify(fallbackOrg));
      } else {
        setCurrentOrganizationState(null);
        await secureStorage.deleteItem(CURRENT_ORG_KEY);
      }
    } catch (error) {
      console.error('Failed to handle org access denied:', error);
    } finally {
      orgDeniedInFlight.current = false;
    }
  }, []);

  const handleOrgAccessDeniedRef = useRef(handleOrgAccessDenied);
  handleOrgAccessDeniedRef.current = handleOrgAccessDenied;

  const isAuthenticated = !!auth0User && !!backendUser;
  const isSuperAdmin = backendUser?.platformRole === 'super_admin';

  useEffect(() => {
    if (isAuthenticated) {
      void registerForPushNotifications().catch(() => {});
    }
  }, [isAuthenticated]);

  const value = useMemo<AuthContextType>(
    () => ({
      auth0User,
      backendUser,
      currentOrganization,
      isLoading,
      isAuthenticated,
      isSuperAdmin,
      requestMagicLink,
      submitMagicLinkCode,
      magicLinkState,
      resetMagicLinkState,
      logout,
      getAccessToken,
      setCurrentOrganization,
      refreshUser,
    }),
    [
      auth0User,
      backendUser,
      currentOrganization,
      isLoading,
      isAuthenticated,
      isSuperAdmin,
      requestMagicLink,
      submitMagicLinkCode,
      magicLinkState,
      resetMagicLinkState,
      logout,
      getAccessToken,
      setCurrentOrganization,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
